import { useState, useEffect } from 'react';
import { useConnectionMode } from '@/connection/SimpleConnectionProvider';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle2, XCircle, Info } from 'lucide-react';

interface BundleCheck {
  name: string;
  check: () => boolean;
  expected: boolean;
  description: string;
}

export const BundleVerification = () => {
  const mode = useConnectionMode();
  const [checks, setChecks] = useState<(BundleCheck & { passed: boolean })[]>([]);
  const isLite = mode === 'lite' || mode === 'offline';

  useEffect(() => {
    const bundleChecks: BundleCheck[] = [
      {
        name: 'No Framer Motion',
        check: () => {
          // Check if framer-motion is in any loaded scripts
          const scripts = Array.from(document.querySelectorAll('script[src]'));
          return !scripts.some(script => 
            script.getAttribute('src')?.includes('framer') || 
            script.getAttribute('src')?.includes('motion')
          );
        },
        expected: true,
        description: 'Framer Motion should not be loaded in Lite mode'
      },
      {
        name: 'Static Skeletons',
        check: () => {
          // Check if skeleton CSS classes exist
          const styles = Array.from(document.styleSheets);
          return styles.some(sheet => {
            try {
              const rules = Array.from(sheet.cssRules || []);
              return rules.some(rule => 
                rule.cssText?.includes('skeleton-shimmer') ||
                rule.cssText?.includes('skeleton-card')
              );
            } catch {
              return false;
            }
          });
        },
        expected: true,
        description: 'Static CSS skeleton loaders should be present'
      },
      {
        name: 'CSS Animations Only',
        check: () => {
          // Verify no JS-based animation libraries
          const scripts = Array.from(document.querySelectorAll('script[src]'));
          const hasHeavyAnimations = scripts.some(script => {
            const src = script.getAttribute('src') || '';
            return src.includes('gsap') || 
                   src.includes('anime') || 
                   src.includes('velocity') ||
                   src.includes('motion');
          });
          return !hasHeavyAnimations;
        },
        expected: true,
        description: 'Should only use CSS animations, no heavy JS libraries'
      },
      {
        name: 'Lite Bundle Size',
        check: () => {
          // Check total script size (rough estimate)
          const scripts = Array.from(document.querySelectorAll('script[src]'));
          const totalScripts = scripts.length;
          // Lite mode should have fewer scripts
          return isLite ? totalScripts < 15 : totalScripts >= 15;
        },
        expected: true,
        description: isLite ? 'Lite mode should load fewer scripts' : 'Full mode can load more scripts'
      }
    ];

    const results = bundleChecks.map(check => ({
      ...check,
      passed: check.check() === check.expected
    }));

    setChecks(results);
  }, [mode, isLite]);

  const allPassed = checks.every(c => c.passed);
  const passedCount = checks.filter(c => c.passed).length;

  if (import.meta.env.PROD) return null; // Only show in development

  return (
    <Card className="border-2 border-dashed">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Info className="w-5 h-5" />
          Bundle Verification - {mode.toUpperCase()} Mode
          <Badge variant={allPassed ? 'default' : 'destructive'}>
            {passedCount}/{checks.length} Passed
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {checks.map((check, i) => (
          <div key={i} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
            {check.passed ? (
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            ) : (
              <XCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            )}
            <div className="flex-1">
              <div className="font-medium">{check.name}</div>
              <div className="text-sm text-muted-foreground">{check.description}</div>
            </div>
          </div>
        ))}
        
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
          <div className="text-sm font-medium mb-1">💡 Verification Tips</div>
          <ul className="text-xs text-muted-foreground space-y-1">
            <li>• Open Network tab to verify no framer-motion chunks</li>
            <li>• Check bundle size: Lite should be ~30% smaller</li>
            <li>• Verify skeletons use CSS shimmer, not JS animations</li>
            <li>• Enable airplane mode to test offline skeleton loaders</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
